import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { useSnackbar } from "notistack5";

import useWebotServer from "hooks/useWebotServer";
import useWhatsappNumbers from "hooks/useWhatsappNumbers";
import { formatPhoneLocal } from "utils/whatsapp/formatPhone";
import { templateNeedsListing } from "components/_dashboard/whatsappOfficial/templates/templateHelpers";
import TemplateListingSection from "components/_dashboard/whatsappOfficial/templates/TemplateListingSection";
import TemplateListingFields from "components/_dashboard/whatsappOfficial/templates/TemplateListingFields";
import useTemplateListing from "components/_dashboard/whatsappOfficial/templates/useTemplateListing";
import { templateNeedsPicture, templateOffersListing, templateRequiresListing, templateWantsListing } from "components/_dashboard/whatsappOfficial/templates/templateListing";
import { isAutoResolvableBinding, isListingBinding, isListingFieldBinding } from "../../../../@types/sharedSchema";
import type { REST_schema } from "../../../../@types/sharedSchema";
import { serverFailureMessage } from "utils/webotServerFailure";

type TemplateView = REST_schema.WhatsappTemplateView;

export type ScheduledSendContact = {
  /** E.164 — the contact doc id. */
  phone: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
};

type Props = {
  open: boolean;
  contact: ScheduledSendContact;
  /** Preselect a template (the template card's "שליחה לאיש קשר" entry). */
  templateDocId?: string;
  onClose: () => void;
  /** Fired after a send was created — hosts refresh their pending badge/section. */
  onScheduled?: () => void;
};

/**
 * One-tap schedule presets — the same gesture as the task quick-add's "מחר / בעוד שבוע"
 * chips. A send preset lands at 09:00, the opening of the send window.
 */
const SCHEDULE_PRESETS: { key: string; label: string; resolve: () => Date }[] = [
  {
    key: "tomorrow_morning",
    label: "מחר בבוקר",
    resolve: () => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      return d;
    }
  },
  {
    key: "next_week",
    label: "בעוד שבוע",
    resolve: () => {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      d.setHours(9, 0, 0, 0);
      return d;
    }
  },
  {
    key: "next_month",
    label: "בעוד חודש",
    resolve: () => {
      const d = new Date();
      d.setMonth(d.getMonth() + 1);
      d.setHours(9, 0, 0, 0);
      return d;
    }
  }
];

/** "HH:mm" in 15-minute steps, the whole day — the window warning handles out-of-window picks. */
export const TIME_OPTIONS: string[] = Array.from({ length: 24 * 4 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
});

export const toDateInputValue = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Is this instant inside the send window (09:00–20:00 Asia/Jerusalem, not Fri-eve/Sat)? */
export const withinIsraelSendWindow = (date: Date): boolean => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Jerusalem",
    hour: "numeric",
    hour12: false,
    weekday: "short"
  }).formatToParts(date);
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "12");
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
  if (weekday === "Sat") return false;
  if (weekday === "Fri" && hour >= 14) return false;
  return hour >= 9 && hour < 20;
};

/**
 * שליחת תבנית לאיש קשר — "now or at a time": a scheduled SINGLE send, riding the campaign engine
 * server-side (`one_off`). Param fields prefill from the contact where the template binds them;
 * everything stays editable. A time outside the send window is accepted with a warning — the
 * server holds the send to the next window rather than refusing.
 */
export default function ScheduledTemplateSendDialog({ open, contact, templateDocId, onClose, onScheduled }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const { whatsappGetTemplates, whatsappScheduledSendCreate } = useWebotServer();
  const { numbers, selected: selectedNumber, selectNumber } = useWhatsappNumbers();

  const [templates, setTemplates] = useState<TemplateView[] | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [when, setWhen] = useState<"now" | "later">("now");
  // Date + time as two fields (a native date input and a 15-minute time select) — the raw
  // datetime-local widget was the hardest control in the dialog to operate on mobile.
  const [scheduledDay, setScheduledDay] = useState("");
  const [scheduledTime, setScheduledTime] = useState("09:00");
  const [busy, setBusy] = useState(false);

  const applyPreset = (at: Date) => {
    setWhen("later");
    setScheduledDay(toDateInputValue(at));
    setScheduledTime(`${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`);
  };

  const numberKey = selectedNumber?.numberKey;

  useEffect(() => {
    if (!open || !numberKey) return;
    setTemplates(null);
    whatsappGetTemplates(false, numberKey)
      .then((res: { templates: TemplateView[] }) => {
        // A listing-bound template needs a send that carries a listing; this dialog never does.
        const approved = res.templates.filter((t) => t.status === "APPROVED" && !templateNeedsListing(t));
        setTemplates(approved);
        if (templateDocId && approved.some((t) => t.id === templateDocId)) {
          setSelectedTemplateId(templateDocId);
        }
      })
      .catch(() => {
        setTemplates([]);
        enqueueSnackbar("טעינת התבניות נכשלה", { variant: "error" });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, numberKey]);

  useEffect(() => {
    if (!open) {
      setSelectedTemplateId("");
      setValues({});
      setWhen("now");
      setScheduledDay("");
      setScheduledTime("09:00");
    }
  }, [open]);

  const template = useMemo(() => templates?.find((t) => t.id === selectedTemplateId), [templates, selectedTemplateId]);

  /**
   * Params the SERVER fills from the contact's own record — never asked for here, and never sent.
   *
   * This dialog used to post a value for EVERY param, resolved by a client-side helper that knew
   * three of the seventeen bindings. That value arrives as `staticParamValues`, which outranks the
   * per-recipient binding for the whole send — so a first name the server would have resolved was
   * permanently frozen to the fallback ("לקוח/ה", "האזור", "-"), silently, for every scheduled
   * one-off. The server drops such entries defensively; not sending them is the honest half.
   */
  const boundParams = useMemo(() => (template?.params ?? []).filter((p) => isAutoResolvableBinding(p.binding)), [template]);
  /** The listing's fields: filled from the listing picked below (editable), like the CRM's and the wizard's send. */
  const listingParams = useMemo(() => (template?.params ?? []).filter((p) => isListingFieldBinding(p.binding)), [template]);
  const manualParams = useMemo(
    () => (template?.params ?? []).filter((p) => !isAutoResolvableBinding(p.binding) && !isListingBinding(p.binding)),
    [template]
  );
  const listingState = useTemplateListing(template, (filled) => setValues((prev) => ({ ...prev, ...filled })));
  const wantsListing = template ? templateWantsListing(template) : false;
  // Only the listing's LINK needs the pick (`templateRequiresListing`); its fields can be typed.
  const requiresListing = template ? templateRequiresListing(template) : false;

  useEffect(() => {
    if (!template) return;
    setValues((prev) => {
      const next = { ...prev };
      for (const param of template.params) {
        // A listing field starts empty: the picked listing fills it.
        if (isAutoResolvableBinding(param.binding) || isListingBinding(param.binding)) {
          continue;
        }
        if (next[param.paramName] === undefined) {
          next[param.paramName] = param.fallbackValue || "";
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id]);

  const preview = useMemo(() => {
    if (!template) return "";
    return template.bodyText.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (whole, token: string) => values[token]?.trim() || whole);
  }, [template, values]);

  const scheduledDate = when === "later" && scheduledDay ? new Date(`${scheduledDay}T${scheduledTime}:00`) : undefined;
  const scheduleInvalid = when === "later" && (!scheduledDate || Number.isNaN(scheduledDate.getTime()) || scheduledDate.getTime() < Date.now());
  const outsideWindow =
    (when === "now" && !withinIsraelSendWindow(new Date())) ||
    (scheduledDate && !Number.isNaN(scheduledDate.getTime()) && !withinIsraelSendWindow(scheduledDate));

  // Bound params are excluded: the server fills them, so requiring a value here would make every
  // template that uses one unsendable from this dialog.
  const canSend =
    Boolean(template) &&
    !busy &&
    !scheduleInvalid &&
    manualParams.every((p) => (values[p.paramName] ?? "").trim().length > 0) &&
    (!requiresListing || Boolean(listingState.listing)) &&
    !listingState.loading &&
    // An image template sends a picture of the listing or an upload, never its approval sample.
    (!template || !templateNeedsPicture(template) || Boolean(listingState.headerImageUrl)) &&
    listingParams.every((p) => (values[p.paramName] ?? "").trim() || p.fallbackValue?.trim());

  const handleSend = async () => {
    if (!template) return;
    setBusy(true);
    try {
      const result = await whatsappScheduledSendCreate({
        templateDocId: template.id,
        numberKey,
        contactIds: [contact.phone],
        // Only what the realtor actually typed (or the listing filled). A bound param is left to the
        // server, and an empty listing field to its fallback.
        staticParamValues: Object.fromEntries([
          ...manualParams.map((p) => [p.paramName, (values[p.paramName] ?? "").trim()]),
          ...listingParams.map((p) => [p.paramName, (values[p.paramName] ?? "").trim()]).filter(([, value]) => value)
        ]),
        ...(listingState.listing ? { listingId: listingState.listing.id } : {}),
        ...(listingState.headerImageUrl ? { headerMediaUrl: listingState.headerImageUrl } : {}),
        ...(when === "later" && scheduledDate ? { scheduledFor: scheduledDate.toISOString() } : {})
      });
      if (result.activation && !result.activation.ok) {
        enqueueSnackbar(result.activation.error ?? "השליחה נוצרה כטיוטה אך ההפעלה נכשלה", { variant: "warning" });
      } else {
        enqueueSnackbar(when === "later" ? "ההודעה תוזמנה" : "ההודעה בדרך", { variant: "success" });
      }
      onScheduled?.();
      onClose();
    } catch (err: any) {
      enqueueSnackbar(serverFailureMessage(err, "השליחה נכשלה"), { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const contactLabel = contact.displayName || [contact.firstName, contact.lastName].filter(Boolean).join(" ") || formatPhoneLocal(contact.phone);

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>שליחת תבנית וואטסאפ — {contactLabel}</DialogTitle>
      <DialogContent>
        {numbers.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
            אין מספר WhatsApp מחובר. חברו מספר בעמוד "WhatsApp רשמי" כדי לשלוח תבניות.
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {numbers.length > 1 && (
              <FormControl fullWidth size="small">
                <InputLabel id="scheduled-send-number-label">מספר שולח</InputLabel>
                <Select
                  labelId="scheduled-send-number-label"
                  label="מספר שולח"
                  value={numberKey ?? ""}
                  onChange={(e) => selectNumber(String(e.target.value))}
                >
                  {numbers.map((n) => (
                    <MenuItem key={n.numberKey} value={n.numberKey}>
                      {n.verifiedName ?? n.displayPhoneNumber ?? n.numberKey}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {templates === null ? (
              <Box sx={{ display: "grid", placeItems: "center", py: 3 }}>
                <CircularProgress size={26} />
              </Box>
            ) : templates.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                אין תבניות מאושרות במספר הזה. צרו תבנית בלשונית "תבניות" — האישור של Meta לוקח
                בדרך כלל עד שעה.
              </Typography>
            ) : (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel id="scheduled-send-template-label">תבנית</InputLabel>
                  <Select
                    labelId="scheduled-send-template-label"
                    label="תבנית"
                    value={selectedTemplateId}
                    onChange={(e) => {
                      setSelectedTemplateId(String(e.target.value));
                      setValues({});
                    }}
                  >
                    {templates.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {t.hebrewTitle || t.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                            {t.bodyText.slice(0, 80)}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {template && (templateOffersListing(template) || template.headerFormat === "VIDEO") && (
                  <TemplateListingSection template={template} state={listingState} required={wantsListing} optional={!requiresListing} disabled={busy} />
                )}

                {template && (
                  <TemplateListingFields
                    params={listingParams}
                    values={values}
                    onChange={(paramName, value) => setValues((prev) => ({ ...prev, [paramName]: value }))}
                    listingPicked={Boolean(listingState.listing)}
                    disabled={busy}
                  />
                )}

                {manualParams.map((param) => (
                  <TextField
                    key={param.paramName}
                    size="small"
                    fullWidth
                    label={param.hebrewLabel || param.paramName}
                    value={values[param.paramName] ?? ""}
                    onChange={(e) => setValues((prev) => ({ ...prev, [param.paramName]: e.target.value }))}
                  />
                ))}

                {boundParams.length > 0 && (
                  <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "start" }}>
                    ימולא אוטומטית: {boundParams.map((p) => p.hebrewLabel).join(", ")}
                    {boundParams.some((p) => p.binding === "listing.link") &&
                      ". הקישור פותח את הנכס במיני-סייט של הלקוח, ואם אין לו, ייווצר אחד בשליחה."}
                  </Typography>
                )}

                {template && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.grey[500], 0.08),
                      border: `1px dashed ${theme.palette.divider}`
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
                      כך ההודעה תיראה:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", textAlign: "start" }}>
                      {preview}
                    </Typography>
                  </Box>
                )}

                <RadioGroup
                  row
                  value={when}
                  onChange={(e) => {
                    const next = e.target.value as "now" | "later";
                    setWhen(next);
                    // Flipping to "later" with nothing picked starts from tomorrow 09:00
                    // instead of an empty, invalid form.
                    if (next === "later" && !scheduledDay) applyPreset(SCHEDULE_PRESETS[0].resolve());
                  }}
                >
                  <FormControlLabel value="now" control={<Radio size="small" />} label="שליחה עכשיו" />
                  <FormControlLabel value="later" control={<Radio size="small" />} label="במועד מאוחר יותר" />
                </RadioGroup>

                <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
                  {SCHEDULE_PRESETS.map((preset) => (
                    <Button
                      key={preset.key}
                      size="small"
                      variant="outlined"
                      color="inherit"
                      sx={{ borderRadius: 4 }}
                      onClick={() => applyPreset(preset.resolve())}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </Stack>

                {when === "later" && (
                  <Stack direction="row" spacing={1}>
                    <TextField
                      type="date"
                      size="small"
                      label="תאריך"
                      sx={{ flexGrow: 1 }}
                      value={scheduledDay}
                      onChange={(e) => setScheduledDay(e.target.value)}
                      error={scheduleInvalid && Boolean(scheduledDay)}
                      helperText={scheduleInvalid && scheduledDay ? "המועד חייב להיות בעתיד" : undefined}
                      InputLabelProps={{ shrink: true }}
                    />
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                      <InputLabel id="scheduled-send-time-label">שעה</InputLabel>
                      <Select
                        labelId="scheduled-send-time-label"
                        label="שעה"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(String(e.target.value))}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                      >
                        {TIME_OPTIONS.map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                )}

                {outsideWindow && (
                  <Alert severity="info">
                    הודעות נשלחות בין 09:00 ל-20:00 (לא בשבת). הודעה שמתוזמנת מחוץ לחלון תישלח
                    בתחילת החלון הבא.
                  </Alert>
                )}

                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  בשליחה אתם מאשרים שיש לכם קשר עסקי קיים עם הנמען או הסכמה שלו לקבלת ההודעה.
                </Typography>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" disabled={busy} onClick={onClose}>
          ביטול
        </Button>
        <Button
          variant="contained"
          disabled={!canSend}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={handleSend}
        >
          {busy ? (when === "later" ? "מתזמן..." : "שולח...") : when === "later" ? "תזמון" : "שליחה"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
